import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../context/authcontext";
import {
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import CreatableSelect from "react-select/creatable";
import { DealService } from "./deal.service";
import { getLifecycleTypeName } from "../../../utils/lifecycleUtils";

interface MarkStatusModalProps {
  isVisible?: boolean;
  onClose: () => void;
  dealId?: number;
  entityId?: number;
  entityType?: "deal" | "lead" | "pipeline";
  tenantId?: number;
  currentStatusReasonId?: number;
  onSuccess: () => void;
}

const MarkStatusModal: React.FC<MarkStatusModalProps> = ({
  isVisible = true,
  onClose,
  dealId,
  entityId,
  entityType = "deal",
  tenantId,
  currentStatusReasonId,
  onSuccess,
}) => {
  const { selectedTenantId, auth } = useContext(AuthContext);

  const [markStatusTab, setMarkStatusTab] = useState<'LOST' | 'WON'>('LOST');
  const [statusReasonSearch, setStatusReasonSearch] = useState('');
  const [debouncedStatusReasonSearch, setDebouncedStatusReasonSearch] = useState('');
  const [statusReasons, setStatusReasons] = useState<any[]>([]);
  const [isSearchingReason, setIsSearchingReason] = useState(false);
  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null);
  const [newReasonName, setNewReasonName] = useState('');
  const [isCreatingReason, setIsCreatingReason] = useState(false);

  const [crmEntityId, setCrmEntityId] = useState<number | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [existingReasonName, setExistingReasonName] = useState('');
  const [existingReason, setExistingReason] = useState<{ id: number; name: string; categoryKey: 'LOST' | 'WON' } | null>(null);

  const resetSelection = () => {
    setSelectedReasonId(null);
    setNewReasonName('');
    setStatusReasonSearch('');
    setStatusReasons([]);
    setExistingReasonName('');
    setExistingReason(null);
  };

  const switchTab = (tab: 'LOST' | 'WON') => {
    setMarkStatusTab(tab);
    setNewReasonName('');
    setStatusReasonSearch('');
    setStatusReasons([]);
    if (existingReason && existingReason.categoryKey === tab) {
      setSelectedReasonId(existingReason.id);
      setExistingReasonName(existingReason.name);
    } else {
      setSelectedReasonId(null);
      setExistingReasonName('');
    }
  };

  useEffect(() => {
    if (!isVisible) {
      resetSelection();
      setMarkStatusTab('LOST');
      return;
    }
  }, [isVisible]);

  useEffect(() => {
    const fetchExistingReason = async () => {
      const actualId = entityId || dealId;
      if (!isVisible || !crmEntityId || !actualId || !selectedTenantId) return;
      try {
        const existing = await DealService.getDealStatusReason(Number(selectedTenantId), crmEntityId, actualId);
        if (existing) {
          const reasonDetails = existing.lifecycle_status_reason;
          const categoryKey = reasonDetails?.lifecycle_status_reason_category?.lifecycle_status_reason_category_key;
          const reasonId = existing.lifecycle_status_reason_id;
          const rName = reasonDetails?.reason_name;

          if (reasonId && rName && (categoryKey === 'LOST' || categoryKey === 'WON')) {
            setExistingReason({ id: reasonId, name: rName, categoryKey });
            setSelectedReasonId(reasonId);
            setExistingReasonName(rName);
            setMarkStatusTab(categoryKey);
          }
        }
      } catch (e) {
        console.error("Error fetching existing reason", e);
      }
    };
    fetchExistingReason();
  }, [isVisible, crmEntityId, dealId, entityId, selectedTenantId]);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [metadataEntityId, categoryList] = await Promise.all([
          DealService.getCrmEntityId(entityType.toUpperCase()),
          DealService.getLifecycleStatusReasonCategories(),
        ]);
        if (metadataEntityId) {
          setCrmEntityId(metadataEntityId);
        }
        if (categoryList.length > 0) {
          setCategories(categoryList);
        }
      } catch (error) {
        console.error("Error fetching metadata", error);
      }
    };
    if (selectedTenantId && isVisible) {
      fetchMetadata();
    }
  }, [selectedTenantId, isVisible, entityType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStatusReasonSearch(statusReasonSearch);
    }, 1000);
    return () => clearTimeout(timer);
  }, [statusReasonSearch]);

  useEffect(() => {
    if (!isVisible || !selectedTenantId) return;

    const currentCategory = categories.find(c => c.lifecycle_status_reason_category_key === markStatusTab);

    const fetchReasons = async () => {
      setIsSearchingReason(true);
      try {
        const results = await DealService.searchLifecycleStatusReasons(Number(selectedTenantId), {
          reasonName: debouncedStatusReasonSearch || undefined,
          categoryId: currentCategory?.lifecycle_status_reason_category_id,
        });
        setStatusReasons(results);
      } catch (error) {
        console.error("Error fetching reasons", error);
        setStatusReasons([]);
      } finally {
        setIsSearchingReason(false);
      }
    };
    fetchReasons();
  }, [debouncedStatusReasonSearch, isVisible, selectedTenantId, markStatusTab, categories]);

  const handleSubmitMarkStatus = async () => {
    const actualId = entityId || dealId;
    if (!crmEntityId || !actualId) return;

    setIsCreatingReason(true);
    try {
      let payload: any = {
        tenant_id: selectedTenantId,
        crm_entity_id: crmEntityId,
        record_id: actualId,
        created_by: auth?.user_id || 1,
        updated_by: auth?.user_id || 1
      };

      if (selectedReasonId) {
        payload.lifecycle_status_reason_id = selectedReasonId;
      } else if (newReasonName) {
        const category = categories.find(c => c.lifecycle_status_reason_category_key === markStatusTab);
        if (!category) throw new Error("Category not found");

        payload.is_new_reason = "true";
        payload.new_reason_name = newReasonName;
        payload.lifecycle_status_reason_category_id = category.lifecycle_status_reason_category_id;
      } else {
        setIsCreatingReason(false);
        return;
      }

      await DealService.saveDealStatusReason(payload);
      showSuccessAlert("Status marked successfully");
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      showErrorAlert("Failed to mark status");
    } finally {
      setIsCreatingReason(false);
    }
  };

  const handleInputChange = (inputValue: string, { action }: any) => {
    if (action === 'input-change') {
      setStatusReasonSearch(inputValue);
    }
  };

  if (!isVisible) return null;

  // Find the selected option to display in CreatableSelect
  const selectedOption = selectedReasonId
    ? {
      label: statusReasons.find(r => r.lifecycle_status_reason_id === selectedReasonId)?.reason_name || existingReasonName || '',
      value: selectedReasonId
    }
    : newReasonName
      ? { label: newReasonName, value: newReasonName }
      : null;

  // Filter out options based on the currently selected tab (if category info is available in reasons)
  // For now, since the API `/lifecycle-status-reason/` might return all reasons, we just show them.
  const selectOptions = statusReasons.map(r => ({
    label: r.reason_name,
    value: r.lifecycle_status_reason_id
  }));

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block" }}
        role="dialog"
        onClick={onClose}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: "500px" }}
        >
          <div className="modal-content shadow-lg border-0 rounded-4" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header border-0 pb-0 pt-4 px-4">
              <h4 className="modal-title d-flex align-items-center fw-bold">
                <div className="avatar avatar-sm bg-light-info text-info rounded-circle me-3 d-flex align-items-center justify-content-center">
                  <i className="ti ti-trophy fs-20" />
                </div>
                Mark as Lost/Won
              </h4>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              />
            </div>

            <div className="modal-body px-4 pt-4 pb-2">
              <div className="d-flex flex-wrap gap-2 mb-4 justify-content-center">
                <button
                  type="button"
                  className={`btn btn-sm d-flex align-items-center px-4 py-2 rounded-pill transition-all ${markStatusTab === 'LOST' ? 'btn-secondary text-white shadow-sm' : 'btn-outline-light text-muted border-light-subtle'}`}
                  onClick={() => switchTab('LOST')}
                  style={{
                    minWidth: 'fit-content',
                    fontSize: '0.875rem',
                    fontWeight: markStatusTab === 'LOST' ? 600 : 500,
                    transform: markStatusTab === 'LOST' ? 'translateY(-1px)' : 'none',
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <i className="ti ti-thumb-down me-2" style={{ fontSize: '1rem' }} />
                  {getLifecycleTypeName("DEAL")} Lost
                </button>
                <button
                  type="button"
                  className={`btn btn-sm d-flex align-items-center px-4 py-2 rounded-pill transition-all ${markStatusTab === 'WON' ? 'btn-success text-white shadow-sm' : 'btn-outline-light text-muted border-light-subtle'}`}
                  onClick={() => switchTab('WON')}
                  style={{
                    minWidth: 'fit-content',
                    fontSize: '0.875rem',
                    fontWeight: markStatusTab === 'WON' ? 600 : 500,
                    transform: markStatusTab === 'WON' ? 'translateY(-1px)' : 'none',
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <i className="ti ti-thumb-up me-2" style={{ fontSize: '1rem' }} />
                  {getLifecycleTypeName("DEAL")} Won
                </button>
              </div>

              <div className="mb-4">
                <label className="form-label fw-semibold text-muted mb-2">
                  {markStatusTab === 'LOST' ? 'Select or create a reason for loss' : 'Select or create a reason for winning'}
                </label>
                <CreatableSelect
                  isLoading={isSearchingReason}
                  options={selectOptions}
                  onInputChange={handleInputChange}
                  filterOption={() => true} // Let backend handle filtering, or if no search is typed, we show all reasons
                  value={selectedOption}
                  onChange={(selected: any) => {
                    if (!selected) {
                      setSelectedReasonId(null);
                      setNewReasonName('');
                    } else if (selected.__isNew__ || typeof selected.value === 'string') {
                      setSelectedReasonId(null);
                      setNewReasonName(selected.label);
                    } else {
                      setSelectedReasonId(selected.value);
                      setNewReasonName('');
                    }
                  }}
                  placeholder="Type to search or create..."
                  className="react-select-custom"
                  classNamePrefix="react-select"
                  formatCreateLabel={(inputValue) => `Create new reason: "${inputValue}"`}
                  styles={{
                    control: (base) => ({
                      ...base,
                      padding: '4px',
                      borderRadius: '8px',
                      borderColor: '#e2e8f0',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#cbd5e1'
                      }
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused ? '#f8fafc' : 'transparent',
                      color: state.isFocused ? '#0f172a' : '#334155',
                      cursor: 'pointer',
                      padding: '10px 12px'
                    })
                  }}
                />
              </div>
            </div>

            <div className="modal-footer border-0 pt-0 px-4 pb-4">
              <button
                type="button"
                className="btn btn-light rounded-pill px-4"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn rounded-pill px-4 ${markStatusTab === 'WON' ? 'btn-success' : 'btn-secondary'}`}
                onClick={handleSubmitMarkStatus}
                disabled={isCreatingReason || (!selectedReasonId && !newReasonName)}
              >
                {isCreatingReason ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Saving...</>
                ) : (
                  <><i className="ti ti-device-floppy me-2" /> Save Status</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} style={{ opacity: 0.5 }} />
    </>
  );
};

export default MarkStatusModal;
